import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledTooltip,
  UncontrolledDropdown,
} from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';
import { injectIntl } from 'react-intl';

import { showModal } from '../../actions';
import { showConfirmationModal } from '../../actions/App';
import {
  updateCollectionOnCollectionsList,
  deleteCollections,
  updateSidebarCollection,
  setSidebarCollectionOpen,
  setCollectionFeaturedImage,
  subscribeCollection,
  unsubscribeCollection,
  showCollectionMoveModal,
  deleteCollectionsPermanent,
} from '../../actions/Collections';

import UpgradePlanButton from '../UpgradePlanButton';

import { isTrash } from '../../helpers/App';
import {
  userCan,
  userHasAccessToken,
  userHasCap,
} from '../../helpers/Auth';
import { getParentCollection } from '../../helpers/Collections';
import { userHasFeature } from '../../helpers/Features';
import { addFlashMessage } from '../../helpers/FlashMessages';
import { getSetting } from '../../helpers/Settings';
import { upgradeModalIfNeeded } from '../../helpers/UpgradePlan';
import { APIconvertCollectionToRegular } from '../../api/Collections';

class CollectionsActions extends PureComponent {
  constructor(props) {
    super(props);

    this.handleRemoveCollection = this.handleRemoveCollection.bind(this);
    this.handleRestoreDeletedCollection = this.handleRestoreDeletedCollection.bind(this);
    this.handleConvertToRegular = this.handleConvertToRegular.bind(this);
  }

  handleRemoveCollection() {
    const {
      intl,
      collection,
      removeCollections,
      openRemoveCollectionModal,
    } = this.props;
    const progressFilesUuid = uuidv4();
    const modalParams = {
      collections: [collection],
      progressFilesUuid,
      onConfirm: (withFiles = false) => (
        removeCollections({
          collectionUuidList: [collection.uuid],
          progressFilesUuid,
          withFiles,
        }).then((response) => {
          const { data } = response;
          if (data && data.errors) {
            addFlashMessage({
              type: 'error',
              text: intl.formatMessage({ defaultMessage: 'An error occurred while deleting collection' }),
            });
          } else {
            const button = {
              url: '/trash',
              text: intl.formatMessage({ defaultMessage: 'Browse trash' }),
            };
            addFlashMessage({
              type: 'success',
              text: intl.formatMessage({ defaultMessage: 'Collection has been deleted' }),
              button,
            });
          }
        })
      ),
    };
    openRemoveCollectionModal(modalParams);
  }

  handleRemoveCollectionPermanent() {
    const {
      intl,
      collection,
      removeCollectionsPermanent,
      openRemoveCollectionModal,
    } = this.props;
    const progressFilesUuid = uuidv4();
    const modalParams = {
      collections: [collection],
      progressFilesUuid,
      mode: 'permanent',
      onConfirm: (withFiles = false) => (
        removeCollectionsPermanent({
          collectionUuidList: [collection.uuid],
          progressFilesUuid,
          withFiles,
        }).then((response) => {
          const { data } = response;
          if (data && data.errors) {
            addFlashMessage({
              type: 'error',
              text: intl.formatMessage({ defaultMessage: 'An error occurred while permanently deleting collection' }),
            });
          } else {
            addFlashMessage({
              type: 'success',
              text: intl.formatMessage({ defaultMessage: 'Collection has been deleted permanently' }),
            });
          }
        })
      ),
    };
    openRemoveCollectionModal(modalParams);
  }

  handleRestoreDeletedCollection(collection) {
    const {
      openCollectionRestoreModal,
    } = this.props;

    openCollectionRestoreModal({
      restoringCollections: [collection.uuid],
      initialParentUuid:
        collection?.attributes?.parent_before_delete ?
          collection?.attributes?.parent_before_delete : null,
    });
  }

  handleConvertToRegular(collection) {
    const { intl, openConfirmationModal } = this.props;
    const modalParams = {
      title: intl.formatMessage({
        defaultMessage: 'Are you sure?',
      }),
      text: intl.formatMessage({
        defaultMessage: 'Do you want to convert this Collection from Smart to Regular?',
      }),
      confirmText: intl.formatMessage({
        defaultMessage: 'Convert',
      }),
      processingText: intl.formatMessage({
        defaultMessage: 'Converting',
      }),
      onConfirm: () => (
        APIconvertCollectionToRegular(collection.uuid).then(() => {
          addFlashMessage({
            type: 'success',
            text: intl.formatMessage({ defaultMessage: 'Collection will be converted to regular' }),
          });
        })
      ),
    };
    openConfirmationModal(modalParams);
  }

  render() {
    const {
      intl,
      profile,
      collection,
      openCollectionEditModal,
      openCollectionDuplicateModal,
      openFileSelectModal,
      updateCollectionOnList,
      handleDownloadCollection,
      handleDownloadRequest,
      handleRequestDownloadCollection,
      handleShareCollection,
      handleShareRequest,
      handleChangeOwner,
      handleChangeAccess,
      handleArchive,
      handleOpenSidebarCollection,
      handleSetCollectionThumbnail,
      handleSubscribe,
      handleUnsubscribe,
      handleMoveCollectionToCollection,
      handleOpenBulkVersioningModal,
      handleSync,
      collectionsView,
      share,
    } = this.props;

    const tooltipParams = {
      fade: false,
      placement: 'bottom',
      trigger: 'hover',
    };

    if (['grid', 'framed'].includes(collectionsView)) {
      return isTrash() ? (
        (userCan('collection.delete', collection) && (
          <div className="collection-actions">
            <ul className="collection-actions-list">
              <li>
                <a
                  href="#restore-from-trash"
                  id={`CollectionActionRestoreFromTrash-${collection.uuid}`}
                  onClick={(e) => {
                    e.preventDefault();
                    this.handleRestoreDeletedCollection(collection);
                  }}
                  aria-label={intl.formatMessage({ defaultMessage: 'Restore' })}
                >
                  <i className="far fa-fw fa-trash-undo" />
                </a>
                <UncontrolledTooltip target={`CollectionActionRestoreFromTrash-${collection.uuid}`} {...tooltipParams}>
                  {intl.formatMessage({ defaultMessage: 'Restore' })}
                </UncontrolledTooltip>
              </li>
              {userHasCap('delete_files_collections_permanent') && (
                <li>
                  <a
                    href="#delete-permanently"
                    id={`CollectionActionDeletePermanently-${collection.uuid}`}
                    onClick={(e) => {
                      e.preventDefault();
                      this.handleRemoveCollectionPermanent();
                    }}
                    aria-label={intl.formatMessage({ defaultMessage: 'Delete permanently' })}
                  >
                    <i className="fas fa-fw fa-trash" />
                  </a>
                  <UncontrolledTooltip target={`CollectionActionDeletePermanently-${collection.uuid}`} {...tooltipParams}>
                    {intl.formatMessage({ defaultMessage: 'Delete permanently' })}
                  </UncontrolledTooltip>
                </li>
              )}
            </ul>
          </div>
        ))
      ) : (
        <div className="collection-actions">
          <ul className="collection-actions-list">
            {(!share && ((userCan('collection.download', collection) || userCan('collection.request_download', collection)) && collection.request)) && (
              <li>
                <a
                  href="#download-requested"
                  id={`CollectionActionDownloadRequest-${collection.uuid}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleDownloadRequest(collection);
                  }}
                  aria-label={intl.formatMessage({ defaultMessage: 'Download' })}
                >
                  <i className="far fa-download" />
                </a>
                <UncontrolledTooltip target={`CollectionActionDownloadRequest-${collection.uuid}`} {...tooltipParams}>
                  {intl.formatMessage({ defaultMessage: 'Download' })}
                </UncontrolledTooltip>
              </li>
            )}
            {(!share && userCan('collection.request_download', collection)) && (
              <li>
                <a
                  href="#request-download"
                  id={`CollectionActionRequestDownload-${collection.uuid}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleRequestDownloadCollection(collection);
                  }}
                  aria-label={intl.formatMessage({ defaultMessage: 'Request Download' })}
                >
                  <i className="far fa-cart-arrow-down" />
                </a>
                <UncontrolledTooltip target={`CollectionActionRequestDownload-${collection.uuid}`} {...tooltipParams}>
                  {intl.formatMessage({ defaultMessage: 'Request Download' })}
                </UncontrolledTooltip>
              </li>
            )}
            {((share && share.allow_download) || (!share && userCan('collection.download', collection) && !collection.request)) && (
              <li>
                <a
                  href="#download"
                  id={`CollectionActionDownload-${collection.uuid}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleDownloadCollection(collection, share);
                  }}
                  aria-label={intl.formatMessage({ defaultMessage: 'Download' })}
                >
                  <i className="far fa-download" />
                </a>
                <UncontrolledTooltip target={`CollectionActionDownload-${collection.uuid}`} {...tooltipParams}>
                  {intl.formatMessage({ defaultMessage: 'Download' })}
                </UncontrolledTooltip>
              </li>
            )}
            {(!share && (userCan('collection.share', collection) && (!collection.archived || getSetting('cs1')))) ? (
              <li>
                <a
                  href="#share"
                  id={`CollectionActionShare-${collection.uuid}`}
                  onClick={(e) => {
                    e.preventDefault();
                    if (userCan('collection.change_access', collection)) {
                      handleChangeAccess(collection.uuid);
                    } else {
                      handleShareCollection(collection);
                    }
                  }}
                  aria-label={userCan('collection.change_access', collection) ? (
                    intl.formatMessage({ defaultMessage: 'Access & Share' })
                  ) : (
                    intl.formatMessage({ defaultMessage: 'Share a link' })
                  )}
                >
                  <i className="far fa-share-alt" />
                </a>
                <UncontrolledTooltip target={`CollectionActionShare-${collection.uuid}`} {...tooltipParams}>
                  {userCan('collection.change_access', collection) ? (
                    intl.formatMessage({ defaultMessage: 'Access & Share' })
                  ) : (
                    intl.formatMessage({ defaultMessage: 'Share a link' })
                  )}
                </UncontrolledTooltip>
              </li>
            ) : (
              <>
                {(!share && userCan('collection.change_access', collection)) && (
                  <li>
                    <a
                      href="#share"
                      id={`CollectionActionShare-${collection.uuid}`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleChangeAccess(collection.uuid);
                      }}
                      aria-label={intl.formatMessage({ defaultMessage: 'Access & Share' })}
                    >
                      <i className="far fa-share-alt" />
                    </a>
                    <UncontrolledTooltip target={`CollectionActionShare-${collection.uuid}`} {...tooltipParams}>
                      {intl.formatMessage({ defaultMessage: 'Access & Share' })}
                    </UncontrolledTooltip>
                  </li>
                )}
              </>
            )}
            {(!share && !userCan('collection.share', collection) && userCan('share_download_requests') && !collection.archived && collection.request) && (
              <li>
                <a
                  href="#share-request"
                  id={`CollectionActionShareRequest-${collection.uuid}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleShareRequest({
                      ...collection.request,
                      user: profile,
                      collections: [collection],
                    });
                  }}
                  aria-label={intl.formatMessage({ defaultMessage: 'Share a link' })}
                >
                  <i className="far fa-share-alt" />
                </a>
                <UncontrolledTooltip target={`CollectionActionShareRequest-${collection.uuid}`} {...tooltipParams}>
                  {intl.formatMessage({ defaultMessage: 'Share a link' })}
                </UncontrolledTooltip>
              </li>
            )}
            {(!share && collection.open && !collection.archived && !collection.smart) && (
              <li>
                <a
                  href="#preview"
                  id={`CollectionActionPreview-${collection.uuid}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleOpenSidebarCollection(collection);
                  }}
                  aria-label={intl.formatMessage({ defaultMessage: 'Open in Collection Builder' })}
                >
                  <i className="far fa-folder" />
                </a>
                <UncontrolledTooltip target={`CollectionActionPreview-${collection.uuid}`} {...tooltipParams}>
                  {intl.formatMessage({ defaultMessage: 'Open in Collection Builder' })}
                </UncontrolledTooltip>
              </li>
            )}
            {(share && ((profile && profile.uuid) || userHasAccessToken())) && (
              <li>
                <a
                  href={`/collections/${collection.uuid}`}
                  id={`CollectionOpen-${collection.uuid}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={intl.formatMessage({ defaultMessage: 'View in workspace' })}
                >
                  <i className="far fa-arrow-up-right-from-square" />
                </a>
                <UncontrolledTooltip target={`CollectionOpen-${collection.uuid}`} {...tooltipParams}>
                  {intl.formatMessage({ defaultMessage: 'View in workspace' })}
                </UncontrolledTooltip>
              </li>
            )}
            {userCan('collection.edit', collection) && (
              <li>
                <a
                  href="#edit"
                  id={`CollectionEdit-${collection.uuid}`}
                  onClick={(e) => {
                    e.preventDefault();
                    openCollectionEditModal({
                      className: 'modal-edit-collection',
                      collection,
                      parentCollection: getParentCollection(collection),
                      afterCreateCollection: updateCollectionOnList,
                    });
                  }}
                  aria-label={intl.formatMessage({ defaultMessage: 'Edit' })}
                >
                  <i className="far fa-edit" />
                </a>
                <UncontrolledTooltip target={`CollectionEdit-${collection.uuid}`} {...tooltipParams}>
                  {intl.formatMessage({ defaultMessage: 'Edit' })}
                </UncontrolledTooltip>
              </li>
            )}
            {(!share && userCan('collection.actions', collection)) && (
              <li className="action-more">
                <UncontrolledDropdown>
                  <DropdownToggle nav className="options" aria-label={intl.formatMessage({ defaultMessage: 'More actions' })}>
                    <i className="far fa-ellipsis-h" />
                  </DropdownToggle>
                  <DropdownMenu right container="body">
                    {(!share && collection.open && !collection.archived && userHasFeature('file-versions') && userCan('file.upload')) && (
                      <DropdownItem
                        onClick={() => {
                          handleOpenBulkVersioningModal(collection);
                        }}
                      >
                        <i className="far fa-rectangle-history-circle-plus" />
                        {intl.formatMessage({ defaultMessage: 'Bulk versioning' })}
                      </DropdownItem>
                    )}
                    {(userCan('collection.edit', collection) && userCan('collection.move', collection)) && (
                      <DropdownItem
                        onClick={() => {
                          handleMoveCollectionToCollection(collection);
                        }}
                      >
                        <i className="fas fa-arrow-alt-right" />
                        {intl.formatMessage({ defaultMessage: 'Move' })}
                      </DropdownItem>
                    )}
                    {userCan('collection.edit', collection) && (
                      <DropdownItem
                        onClick={() => {
                          openCollectionDuplicateModal({
                            className: 'modal-duplicate-collection',
                            collection,
                            parentCollection: getParentCollection(collection),
                          });
                        }}
                      >
                        <i className="fas fa-clone" />
                        {intl.formatMessage({ defaultMessage: 'Duplicate' })}
                      </DropdownItem>
                    )}
                    {userCan('collection.edit', collection) && (
                      <DropdownItem
                        onClick={() => {
                          openFileSelectModal({
                            className: 'modal-select-file',
                            handleSelectFile: (file) => {
                              handleSetCollectionThumbnail(collection, file);
                            },
                          });
                        }}
                      >
                        <i className="fas fa-image" />
                        {intl.formatMessage({ defaultMessage: 'Change Thumbnail' })}
                      </DropdownItem>
                    )}
                    {collection.smart && userCan('collection.edit', collection) && (
                      <DropdownItem
                        onClick={() => {
                          this.handleConvertToRegular(collection);
                        }}
                      >
                        <i className="fas fa-diagram-next" />
                        {intl.formatMessage({ defaultMessage: 'Convert to regular Collection' })}
                      </DropdownItem>
                    )}
                    {userCan('collection.change_owner', collection) && (
                      <DropdownItem
                        onClick={() => {
                          handleChangeOwner(collection);
                        }}
                      >
                        <i className="fas fa-user-circle" />
                        {intl.formatMessage({ defaultMessage: 'Change Owner' })}
                      </DropdownItem>
                    )}
                    <CopyToClipboard
                      text={collection.uuid}
                      options={{
                        format: 'text/plain',
                      }}
                    >
                      <DropdownItem
                        onClick={() => {
                          addFlashMessage({
                            type: 'success',
                            text: intl.formatMessage({ defaultMessage: 'Collection ID has been copied' }),
                          });
                        }}
                      >
                        <i className="fas fa-copy" />
                        {intl.formatMessage({ defaultMessage: 'Copy ID' })}
                      </DropdownItem>
                    </CopyToClipboard>
                    <DropdownItem
                      onClick={() => {
                        if (collection.subscribed) {
                          handleUnsubscribe(collection);
                        } else {
                          handleSubscribe(collection);
                        }
                      }}
                    >
                      {collection.subscribed ? (
                        <>
                          <i className="fas fa-bell-slash" />
                          {intl.formatMessage({ defaultMessage: 'Unsubscribe' })}
                        </>
                      ) : (
                        <>
                          <i className="fas fa-bell" />
                          {intl.formatMessage({ defaultMessage: 'Subscribe' })}
                        </>
                      )}
                    </DropdownItem>
                    {userCan('collection.delete', collection) && (
                      <DropdownItem
                        onClick={(e) => {
                          e.preventDefault();
                          this.handleRemoveCollection(collection);
                        }}
                      >
                        <i className="fas fa-trash" />
                        {intl.formatMessage({ defaultMessage: 'Delete' })}
                      </DropdownItem>
                    )}
                    {userCan('collection.archive', collection) && (
                      <>
                        <DropdownItem divider />
                        <DropdownItem
                          onClick={(e) => upgradeModalIfNeeded('archive', () => handleArchive(collection), e)}
                          className={classnames({ 'inactive silent-pointer-events': !userHasFeature('archive') })}
                        >
                          <i className={`fas fa-${collection.archived ? 'box-open' : 'archive'}`} />
                          {collection.archived ? intl.formatMessage({ defaultMessage: 'Unarchive' }) :
                            intl.formatMessage({ defaultMessage: 'Archive' })}
                          <UpgradePlanButton
                            feature="archive"
                            simple={true}
                          />
                        </DropdownItem>
                      </>
                    )}
                    {(userCan('desktop_sync') && !collection.smart) && (
                      <>
                        <DropdownItem divider />
                        <DropdownItem
                          onClick={() => { handleSync(collection); }}
                        >
                          {collection.synced ? (
                            <>
                              <i className="fas fa-rotate-exclamation" />
                              {intl.formatMessage({ defaultMessage: 'Unsync' })}
                            </>
                          ) : (
                            <>
                              <i className="fas fa-rotate" />
                              {intl.formatMessage({ defaultMessage: 'Sync' })}
                            </>
                          )}
                        </DropdownItem>
                      </>
                    )}
                  </DropdownMenu>
                </UncontrolledDropdown>
              </li>
            )}
          </ul>
        </div>
      );
    }

    if (collectionsView === 'list') {
      return isTrash() ? (
        <UncontrolledDropdown>
          <DropdownToggle nav className="options" aria-label={intl.formatMessage({ defaultMessage: 'More actions' })}>
            <i className="far fa-ellipsis-h" />
          </DropdownToggle>
          <DropdownMenu right container="body" className="list-item-actions-list">
            <DropdownItem
              onClick={() => {
                this.handleRestoreDeletedCollection(collection);
              }}
            >
              <i className="far fa-fw fa-trash-undo" />
              {intl.formatMessage({ defaultMessage: 'Restore' })}
            </DropdownItem>
            {userHasCap('delete_files_collections_permanent') && (
              <DropdownItem
                onClick={() => {
                  this.handleRemoveCollectionPermanent();
                }}
              >
                <i className="fas fa-fw fa-trash" />
                {intl.formatMessage({ defaultMessage: 'Delete permanently' })}
              </DropdownItem>
            )}
          </DropdownMenu>
        </UncontrolledDropdown>
      ) : (
        <UncontrolledDropdown>
          <DropdownToggle nav className="options" aria-label={intl.formatMessage({ defaultMessage: 'More actions' })}>
            <i className="far fa-ellipsis-h" />
          </DropdownToggle>
          <DropdownMenu right container="body" className="list-item-actions-list">
            {((userCan('collection.download', collection) || userCan('collection.request_download', collection)) && collection.request) && (
              <DropdownItem
                onClick={() => {
                  handleDownloadRequest(collection);
                }}
              >
                <i className="fas fa-download" />
                {intl.formatMessage({ defaultMessage: 'Download' })}
              </DropdownItem>
            )}
            {userCan('collection.request_download', collection) && (
              <DropdownItem
                onClick={() => {
                  handleRequestDownloadCollection(collection);
                }}
              >
                <i className="fas fa-cart-arrow-down" />
                {intl.formatMessage({ defaultMessage: 'Request Download' })}
              </DropdownItem>
            )}
            {(userCan('collection.download', collection) && !collection.request) && (
              <DropdownItem
                onClick={() => {
                  handleDownloadCollection(collection, share);
                }}
              >
                <i className="fas fa-download" />
                {intl.formatMessage({ defaultMessage: 'Download' })}
              </DropdownItem>
            )}
            {(userCan('collection.share', collection) && (!collection.archived || getSetting('cs1'))) ? (
              <DropdownItem
                onClick={() => {
                  if (userCan('collection.change_access', collection)) {
                    handleChangeAccess(collection.uuid);
                  } else {
                    handleShareCollection(collection);
                  }
                }}
              >
                <i className="fas fa-share-alt" />
                {userCan('collection.change_access', collection) ? (
                  intl.formatMessage({ defaultMessage: 'Access & Share' })
                ) : (
                  intl.formatMessage({ defaultMessage: 'Share a link' })
                )}
              </DropdownItem>
            ) : (
              <>
                {userCan('collection.change_access', collection) && (
                  <DropdownItem
                    onClick={() => {
                      handleChangeAccess(collection.uuid);
                    }}
                  >
                    <i className="fas fa-share-alt" />
                    {intl.formatMessage({ defaultMessage: 'Access & Share' })}
                  </DropdownItem>
                )}
              </>
            )}
            {(!userCan('collection.share', collection) && userCan('share_download_requests') && !collection.archived && collection.request) && (
              <DropdownItem
                onClick={() => {
                  handleShareRequest({
                    ...collection.request,
                    user: profile,
                    collections: [collection],
                  });
                }}
              >
                <i className="fas fa-share-alt" />
                {intl.formatMessage({ defaultMessage: 'Share a link' })}
              </DropdownItem>
            )}
            {(!share && collection.open && !collection.archived && !collection.smart) && (
              <DropdownItem
                onClick={() => {
                  handleOpenSidebarCollection(collection);
                }}
              >
                <i className="far fa-folder" />
                {intl.formatMessage({ defaultMessage: 'Open in Collection Builder' })}
              </DropdownItem>
            )}
            {(!share && collection.open && !collection.archived && userHasFeature('file-versions') && userCan('file.upload')) && (
              <DropdownItem
                onClick={() => {
                  handleOpenBulkVersioningModal(collection);
                }}
              >
                <i className="far fa-rectangle-history-circle-plus" />
                {intl.formatMessage({ defaultMessage: 'Bulk versioning' })}
              </DropdownItem>
            )}
            {userCan('collection.edit', collection) && (
              <>
                <DropdownItem
                  onClick={() => {
                    openCollectionEditModal({
                      className: 'modal-edit-collection',
                      collection,
                      parentCollection: getParentCollection(collection),
                      afterCreateCollection: updateCollectionOnList,
                    });
                  }}
                >
                  <i className="fas fa-edit" />
                  {intl.formatMessage({ defaultMessage: 'Edit' })}
                </DropdownItem>
                <DropdownItem
                  onClick={() => {
                    openCollectionDuplicateModal({
                      className: 'modal-duplicate-collection',
                      collection,
                      parentCollection: getParentCollection(collection),
                    });
                  }}
                >
                  <i className="fas fa-clone" />
                  {intl.formatMessage({ defaultMessage: 'Duplicate' })}
                </DropdownItem>
                <DropdownItem
                  onClick={() => {
                    openFileSelectModal({
                      className: 'modal-select-file',
                      handleSelectFile: (file) => {
                        handleSetCollectionThumbnail(collection, file);
                      },
                    });
                  }}
                >
                  <i className="fas fa-image" />
                  {intl.formatMessage({ defaultMessage: 'Change Thumbnail' })}
                </DropdownItem>
              </>
            )}
            {userCan('collection.change_owner', collection) && (
              <DropdownItem
                onClick={() => {
                  handleChangeOwner(collection);
                }}
              >
                <i className="fas fa-user-circle" />
                {intl.formatMessage({ defaultMessage: 'Change Owner' })}
              </DropdownItem>
            )}
            <CopyToClipboard
              text={collection.uuid}
              options={{
                format: 'text/plain',
              }}
            >
              <DropdownItem
                onClick={() => {
                  addFlashMessage({
                    type: 'success',
                    text: intl.formatMessage({ defaultMessage: 'Collection ID has been copied' }),
                  });
                }}
              >
                <i className="fas fa-copy" />
                {intl.formatMessage({ defaultMessage: 'Copy ID' })}
              </DropdownItem>
            </CopyToClipboard>
            <DropdownItem
              onClick={() => {
                if (collection.subscribed) {
                  handleUnsubscribe(collection);
                } else {
                  handleSubscribe(collection);
                }
              }}
            >
              {collection.subscribed ? (
                <>
                  <i className="fas fa-bell-slash" />
                  {intl.formatMessage({ defaultMessage: 'Unsubscribe' })}
                </>
              ) : (
                <>
                  <i className="fas fa-bell" />
                  {intl.formatMessage({ defaultMessage: 'Subscribe' })}
                </>
              )}
            </DropdownItem>
            {userCan('collection.delete', collection) && (
              <DropdownItem
                onClick={(e) => {
                  e.preventDefault();
                  this.handleRemoveCollection(collection);
                }}
              >
                <i className="fas fa-trash" />
                {intl.formatMessage({ defaultMessage: 'Delete' })}
              </DropdownItem>
            )}
            {userCan('collection.archive', collection) && (
              <>
                <DropdownItem divider />
                <DropdownItem
                  onClick={(e) => upgradeModalIfNeeded('archive', () => handleArchive(collection), e)}
                  className={classnames({ 'inactive silent-pointer-events': !userHasFeature('archive') })}
                >
                  <i className={`fas fa-${collection.archived ? 'box-open' : 'archive'}`} />
                  {collection.archived ? intl.formatMessage({ defaultMessage: 'Unarchive' }) :
                    intl.formatMessage({ defaultMessage: 'Archive' })}
                  <UpgradePlanButton
                    feature="archive"
                    simple={true}
                  />
                </DropdownItem>
              </>
            )}
            {(userCan('desktop_sync') && !collection.smart) && (
              <>
                <DropdownItem divider />
                <DropdownItem
                  onClick={() => { handleSync(collection); }}
                >
                  {collection.synced ? (
                    <>
                      <i className="fas fa-rotate-exclamation" />
                      {intl.formatMessage({ defaultMessage: 'Unsync' })}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-rotate" />
                      {intl.formatMessage({ defaultMessage: 'Sync' })}
                    </>
                  )}
                </DropdownItem>
              </>
            )}
          </DropdownMenu>
        </UncontrolledDropdown>
      );
    }

    return null;
  }
}

const mapStateToProps = state => ({
  profile: state.Auth.profile,
  selectedFiles: state.Files.selectedFiles,
});

const mapDispatchToProps = dispatch => ({
  openCollectionEditModal: (params) => {
    dispatch(showModal('CollectionEditModal', params));
  },
  openCollectionDuplicateModal: (params) => {
    dispatch(showModal('CollectionDuplicateModal', params));
  },
  openFileSelectModal: (params) => {
    dispatch(showModal('FileSelectModal', params));
  },
  updateCollectionOnList: (collection) => {
    dispatch(updateCollectionOnCollectionsList(collection));
  },
  openConfirmationModal: (params) => {
    dispatch(showConfirmationModal(params));
  },
  openRemoveCollectionModal: (params) => {
    dispatch(showModal('RemoveModal', params));
  },
  openCollectionRestoreModal: (params) => {
    dispatch(showModal('CollectionRestoreModal', params));
  },
  removeCollections: ({ collectionUuidList, progressFilesUuid, withFiles = false }) => (
    dispatch(deleteCollections({
      collectionUuidList,
      progressFilesUuid,
      withFiles,
    }))
  ),
  removeCollectionsPermanent: ({ collectionUuidList, progressFilesUuid, withFiles = false }) => (
    dispatch(deleteCollectionsPermanent({
      collectionUuidList,
      progressFilesUuid,
      withFiles,
    }))
  ),
  handleDownloadCollection: (collection, share) => {
    dispatch(showModal('DownloadModal', {
      className: 'modal-download',
      collections: [collection],
      shareUuid: share ? share.uuid : null,
      requireName: share ? share.require_name : false,
      requireEmail: share ? share.require_email : false,
      backdrop: true,
    }));
  },
  handleDownloadRequest: (collection) => {
    dispatch(showModal('DownloadModal', {
      className: 'modal-download',
      files: collection.request.files,
      collections: [collection],
      requestUuid: collection.request.uuid,
      availableFileSizes: collection.request.file_sizes.map(fs => (`${fs.id}`)),
      backdrop: true,
    }));
  },
  handleRequestDownloadCollection: (collection) => {
    dispatch(showModal('DownloadModal', {
      className: 'modal-download',
      collections: [collection],
      backdrop: true,
      request: true,
    }));
  },
  handleShareCollection: (collection) => {
    dispatch(showModal('ShareModal', {
      className: 'modal-share modal-share-file',
      collections: [collection],
    }));
  },
  handleShareRequest: (request) => {
    dispatch(showModal('ShareModal', {
      className: 'modal-share modal-share-file',
      request,
    }));
  },
  handleChangeOwner: (collection) => {
    dispatch(showModal('ChangeOwnerModal', {
      className: 'modal-change-owner',
      collections: [collection],
    }));
  },
  handleChangeAccess: (collection) => {
    dispatch(showModal('AccessModal', {
      className: 'modal-access modal-file-access',
      collections: [collection],
    }));
  },
  handleArchive: (collection) => {
    dispatch(showModal('ArchiveModal', {
      className: 'modal-archive modal-file-archive',
      collections: [collection],
      action: collection.archived ? 'unarchive' : 'archive',
    }));
  },
  handleOpenBulkVersioningModal: (collection) => {
    dispatch(showModal('BulkVersioningModal', {
      className: 'modal-bulk-versioning',
      collection,
    }));
  },
  handleOpenSidebarCollection: (collection) => {
    dispatch(updateSidebarCollection(collection));
    dispatch(setSidebarCollectionOpen(true));
  },
  handleSetCollectionThumbnail: (collection, file) => {
    dispatch(setCollectionFeaturedImage(collection.uuid, file.uuid));
  },
  handleSubscribe: (collection) => {
    dispatch(subscribeCollection(collection.uuid));
  },
  handleUnsubscribe: (collection) => {
    dispatch(unsubscribeCollection(collection.uuid));
  },
  handleMoveCollectionToCollection: (collection) => {
    dispatch(showCollectionMoveModal(collection));
  },
  handleSync: (collection) => {
    dispatch(showModal('SyncModal', {
      className: 'modal-sync',
      collection,
    }));
  },
});

CollectionsActions.propTypes = {
  intl: PropTypes.object.isRequired,
  profile: PropTypes.object,
  collection: PropTypes.object.isRequired,
  openCollectionEditModal: PropTypes.func.isRequired,
  openCollectionDuplicateModal: PropTypes.func.isRequired,
  openConfirmationModal: PropTypes.func.isRequired,
  openFileSelectModal: PropTypes.func.isRequired,
  openRemoveCollectionModal: PropTypes.func.isRequired,
  openCollectionRestoreModal: PropTypes.func.isRequired,
  removeCollections: PropTypes.func.isRequired,
  removeCollectionsPermanent: PropTypes.func.isRequired,
  updateCollectionOnList: PropTypes.func.isRequired,
  handleDownloadCollection: PropTypes.func.isRequired,
  handleDownloadRequest: PropTypes.func.isRequired,
  handleRequestDownloadCollection: PropTypes.func.isRequired,
  handleShareCollection: PropTypes.func.isRequired,
  handleShareRequest: PropTypes.func.isRequired,
  handleChangeOwner: PropTypes.func.isRequired,
  handleChangeAccess: PropTypes.func.isRequired,
  handleArchive: PropTypes.func.isRequired,
  handleOpenSidebarCollection: PropTypes.func.isRequired,
  handleSetCollectionThumbnail: PropTypes.func.isRequired,
  handleSubscribe: PropTypes.func.isRequired,
  handleUnsubscribe: PropTypes.func.isRequired,
  handleMoveCollectionToCollection: PropTypes.func.isRequired,
  handleOpenBulkVersioningModal: PropTypes.func.isRequired,
  handleSync: PropTypes.func.isRequired,
  collectionsView: PropTypes.string,
  share: PropTypes.object,
};

CollectionsActions.defaultProps = {
  profile: null,
  collectionsView: 'grid',
  share: null,
};

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(CollectionsActions));
